<script lang="ts" setup>
import { NInput, useMessage } from "naive-ui";
import { ref, watch, nextTick, computed } from "vue";

const props = withDefaults(
    defineProps<{
        modelValue: number | null | undefined;
        placeholder?: string;
        livePreview?: boolean;
        showPrefix?: boolean;
        showCopyBtn?: boolean;
        colorByValue?: boolean;
        // if true, only emit update on blur/enter, not while typing
        // useful to avoid sending multiple request to API when value changes
        disableUpdateOnChange?: boolean;
        bordered?: boolean;
        // if true, never show border (even in live preview mode)
        forceNotBorder?: boolean;
    }>(),
    {
        placeholder: "",
        livePreview: false,
        showPrefix: false,
        showCopyBtn: false,
        colorByValue: false,
        disableUpdateOnChange: false,
        bordered: true,
        forceNotBorder: false,
    },
);
const emits = defineEmits<{
    (_e: "update:modelValue", _value: number | null | undefined): void;
}>();

const isEditing = ref(false);
const localValue = ref(props.modelValue != null ? props.modelValue.toString() : "");
const inputRef = ref<any>(null);
const message = useMessage();

watch(
    () => props.modelValue,
    (val) => {
        // keep local in sync when external changes (unless user currently editing?)
        if (!isEditing.value) {
            localValue.value = val != null ? val.toString() : "";
        }
    },
);

function enterEditMode() {
    isEditing.value = true;
    nextTick(() => {
        inputRef.value?.focus();
    });
}

function parseToNumberOrNull(raw: string): number | null {
    const cleaned = raw.trim().replace(/,/g, "");
    if (cleaned === "") return null;
    const num = Number(cleaned);
    return isNaN(num) ? null : num;
}

function emitCommitIfChanged() {
    const num = parseToNumberOrNull(localValue.value ?? "");
    if (num !== props.modelValue) {
        emits("update:modelValue", num);
    }
}

function exitEditMode() {
    // When livePreview is active we only commit when actually exiting edit mode.
    // When livePreview is false (input always visible), still treat blur/enter as commit points.
    if (props.livePreview) {
        if (!isEditing.value) return;
        isEditing.value = false;
    }
    // When updates are disabled during typing, commit now.
    if (props.disableUpdateOnChange) {
        emitCommitIfChanged();
    }
}

function handleKeydown(e: KeyboardEvent) {
    if (e.key === "Enter") {
        exitEditMode();
    }
}

// Reactive validation & emission while user types
watch(
    () => localValue.value,
    (val) => {
        const raw = (val ?? "").toString().trim().replace(/,/g, "");
        if (raw === "") {
            // empty input
            if (!props.disableUpdateOnChange) {
                if (props.modelValue !== null) {
                    emits("update:modelValue", null);
                }
            }
            return;
        }
        const isValid = /^\d*(\.\d{0,2})?$/.test(raw);
        if (!isValid) {
            const match = raw.match(/^\d*(\.\d{0,2})?/);
            if (match) {
                const truncated = match[0];
                if (truncated !== val) {
                    localValue.value = truncated; // sanitize
                }
            } else {
                localValue.value = props.modelValue != null ? props.modelValue.toString() : "";
            }
            return;
        }
        // Valid -> emit only if live updates enabled
        if (!props.disableUpdateOnChange) {
            const num = Number(raw);
            const parsed = isNaN(num) ? null : num;
            if (parsed !== props.modelValue) {
                emits("update:modelValue", parsed);
            }
        }
    },
);

const formattedValue = computed(() => {
    const value = localValue.value;
    if (value === null || value === undefined || value === "") return props.placeholder || "";
    const num = Number(value);
    if (isNaN(num)) return value;
    return num.toLocaleString("en-US", { style: "currency", currency: "USD" });
});

const colorClass = computed(() => {
    if (!props.colorByValue) return "";
    const num = props.modelValue ?? 0;
    if (num > 0) return "text-[#238F6B]";
    if (num < 0) return "text-red";
    return "";
});

function handleCopyValue() {
    const value = localValue.value;
    if (value === null || value === undefined || value === "") {
        return;
    }
    navigator.clipboard.writeText(value);
    message.info("Copied!", {
        duration: 1000,
    });
}

const isCopyValid = computed(() => {
    const value = localValue.value;
    return value !== null && value !== undefined && value !== "";
});

const renderOutline = computed(() => {
    return !props.bordered && !props.livePreview && !props.forceNotBorder;
});
</script>

<template>
    <div class="relative w-full">
        <n-input
            v-if="!props.livePreview || isEditing"
            ref="inputRef"
            v-model:value="localValue"
            :placeholder="placeholder"
            type="text"
            round
            :bordered="bordered"
            class="w-full"
            :class="[
                renderOutline
                    ? 'outline outline-[1px] outline-transparent hover:outline-gray-300 focus-within:outline-blurple-100 focus-within:hover:outline-blurple-100 active:outline-blurple-100'
                    : '',
            ]"
            @blur="exitEditMode"
            @keydown="handleKeydown"
        >
            <template v-if="showPrefix" #prefix>
                <span
                    :class="{
                        'text-neutral-400':
                            localValue === '' || localValue === null || localValue === undefined,
                    }"
                    >$</span
                >
            </template>
        </n-input>
        <p
            v-else
            :class="[
                'cursor-pointer flex items-center px-3 min-h-[34px] rounded-full w-full border border-transparent hover:bg-cream transition-colors duration-150',
                colorClass,
            ]"
            @click="enterEditMode"
        >
            {{ formattedValue }}
        </p>
        <button
            v-if="showCopyBtn"
            :class="[
                'material-symbols-outlined text-gray-300 text-lg absolute top-0.5 right-7 z-10',
                !isCopyValid ? 'cursor-not-allowed opacity-50' : 'active:scale-95',
            ]"
            :disabled="!isCopyValid"
            @click="handleCopyValue"
        >
            content_copy
        </button>
    </div>
</template>
